import { GridSortItem } from "@mui/x-data-grid";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { EncounterReportControllerApi } from "../../services";
import {
  DGT_Pagination,
  getConfigObject,
  getPageIndexWhileSearch,
  getPageSize,
  getXtenantId,
  useBaseContent,
} from "../../utils/utils";

interface UsePatientEncounterReportListProps {
  pagination: DGT_Pagination;
  gridSorting?: GridSortItem[];
  searchKey?: string;
  startDate?: string;
  endDate?: string;
  statuses?: Array<string>;
  visitTypes?: Array<string>;
  providerUuid?: Array<string>;
  locationUuid?: Array<string>;
  providerGroupId?: string | null;
  diagnosisSearch?: string;
}

/**
 * @description Hook for fetching patient encounter report listing data
 */
export function usePatientEncounterReportList(
  props: UsePatientEncounterReportListProps
): useBaseContent<any> {
  const prevPage = useRef<number>(props.pagination.page);

  const pageIndex = getPageIndexWhileSearch({
    pagination: props.pagination,
    searchKey: props.searchKey,
    prevPage,
  });

  const pageSize = getPageSize(props.searchKey, props.pagination);
  const sortData = props.gridSorting?.[0];

  return useQuery({
    queryKey: [
      "patient-encounter-report-list",
      pageSize,
      pageIndex,
      sortData,
      props.searchKey,
      props.startDate,
      props.endDate,
      props.statuses,
      props.visitTypes,
      props.providerUuid,
      props.locationUuid,
      props.diagnosisSearch,
    ],
    enabled: !!props.providerGroupId,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    queryFn: async () =>
      new EncounterReportControllerApi(getConfigObject())
        .getEncounterReport(
          pageIndex,
          pageSize,
          sortData?.field ?? undefined,
          sortData?.sort ?? undefined,
          props.startDate,
          props.endDate,
          props.providerUuid,
          props.locationUuid,
          props.visitTypes,
          props.statuses,
          props.searchKey,
          props.diagnosisSearch,
          props.providerGroupId ?? getXtenantId()
        )
        .then((res) => res?.data),
  });
}
